import React, { useEffect, useRef } from "react";
import { InfoBoxProps } from "../types";
import Image from "next/image";
import Icon from "@/components/icons";
import { iconTypes, IconTypes } from "@/components/icons/types";
import { useInView } from "react-intersection-observer";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { ElementViewed } from "@/hooks/eventing/types/redventures.usertracking.v3.ElementViewed";
import { ElementType } from "@/constants/tagular/main";

function isImageUrl(str: unknown): str is string {
  return (
    typeof str === "string" &&
    /\.(svg|png|jpg|jpeg|gif|webp)(\?|#|$)/i.test(str)
  );
}

export default function InformationalInfoBox({
  icon,
  heading,
  headingLevel = "p",
  location,
  position = "1",
  children,
}: InfoBoxProps) {
  const isIconType = iconTypes.includes(icon as IconTypes);
  const isImage = isImageUrl(icon);

  const webElement: ElementViewed["webElement"] = {
    elementType: ElementType.Component,
    htmlId: "informational-info-box",
    location: location || "informational-info-box",
    name: heading ? heading.replace(/\s+/g, "-").toLowerCase() : "info-box",
    text: heading,
    position: position,
  };

  const { elementViewed } = useElementEvents({ webElement });
  const { inView, ref } = useInView();
  const viewedRef = useRef(false);

  useEffect(() => {
    if (inView && !viewedRef.current) {
      elementViewed();
      viewedRef.current = true;
    }

    if (!inView) {
      viewedRef.current = false;
    }
  }, [inView, elementViewed]);

  return (
    <div
      ref={ref}
      className="bg-putty-200 p-8 rounded-xl flex flex-col lg:flex-row container border border-putty-400"
    >
      {isImage && (
        <div className="block not-prose min-w-[70px] lg:mr-6 lg:mt-4 mb-4">
          <Image src={icon} alt={heading || ""} width={70} height={200} />
        </div>
      )}

      <div className="w-full flex gap-4">
        <div className="flex-row">
          {heading && (
            <div className="flex items-center gap-3">
              {isIconType && <Icon type={icon as IconTypes} />}
              {React.createElement(
                headingLevel,
                { className: "text-primary-500 font-bold !my-0" },
                heading,
              )}
            </div>
          )}
          <div className="mt-2">{children}</div>
        </div>
      </div>
    </div>
  );
}
